import { ReactNode, useCallback, useState, MouseEvent, useMemo } from "react";

import { Typography } from "antd";
import { clsx } from "clsx";

import { useUIConfig } from "../../../config/uiConfigContext";

import styles from "./RVReadMore.module.scss";

function randomString() {
    return (Math.random() + 1).toString(36).substring(7);
}

type RVReadMoreProps = {
    key?: string;
    children?: ReactNode;
    className?: string;
    rows: number;
    hideEllipsesSpan?: boolean;
    fieldDescriptor?: string;
};

/**
 * 
 * Easy drop in component for hiding extra content beyond a certain number of rows (provided in `rows` prop) behind an ellipses
 * see more / see less CTA
 * 
 * @param key: string: Unique key for prop (optional, defaults to a random string)
 * @param children?: ReactNode: standard react children extra components
 * @param className?: string: Extra class for additional or overwritten styles
 * @param rows: number: How many rows to display before hiding extra content behind ellipses
 * @param hideEllipsesSpan boolean: Hide the annoying '...' span that is automatically written when using the ellipses prop for
                                    antd component
*  @param fieldDescriptor?: string: Makes logging in onExpandCallback and onCollapseCallback more specific if provided

 * @returns Typography.Paragraph with hidden rows
 */
export function RVReadMore({
    key = randomString(),
    rows,
    className,
    hideEllipsesSpan = false,
    fieldDescriptor = "text",
    children,
}: RVReadMoreProps) {
    const [elemKey, setElemKey] = useState<string>(key);
    const [expanded, setExpanded] = useState<boolean>(false);

    const { getLogger } = useUIConfig();
    const logger = useMemo(() => getLogger("RVReadMore"), [getLogger]);

    const onExpandCallback = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            e.preventDefault();
            setExpanded(true);

            logger.action(`Clicking See More to expand ${fieldDescriptor}`);
        },
        [fieldDescriptor, logger]
    );

    const onCollapseCallback = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            e.preventDefault();
            setElemKey(randomString());

            logger.action(`Clicking See Less to hide ${fieldDescriptor}`);
        },
        [fieldDescriptor, logger]
    );

    return (
        <div
            key={`read-more-key-${elemKey}`}
            className={clsx(styles.readMoreContainer, { [styles.hideEllipsesSpan]: hideEllipsesSpan })}
        >
            {/* todo check to see if we would rebove the bottom margin from the paragraph globally */}
            <Typography.Paragraph
                className={clsx(styles.readMoreText, className)}
                ellipsis={{
                    rows,
                    expandable: true,
                    symbol: "See More",
                    onExpand: onExpandCallback,
                }}
            >
                <>
                    {children}{" "}
                    {expanded && (
                        <a className="see-less-anchor" onClick={onCollapseCallback}>
                            See Less
                        </a>
                    )}
                </>
            </Typography.Paragraph>
        </div>
    );
}
